import { Box, useTheme } from '@mui/material';

import { HighlightedTextNew } from '@verantos-dev/core-annotation-tool';

interface RenderHighlightedTextProps {
  text: string;
  highlightedTextSpans: HighlightedTextNew[];
  selectedNoteId: string;
}

export const RenderHighlightedText = ({
  text,
  highlightedTextSpans,
  selectedNoteId,
}: RenderHighlightedTextProps) => {
  const theme = useTheme();

  if (!highlightedTextSpans || highlightedTextSpans.length === 0 || selectedNoteId === '') {
    return <>{text}</>;
  }
  const filteredHighlightedTexts = highlightedTextSpans.filter(
    (highlight) => highlight.noteId === selectedNoteId
  );
  // Sort highlights by offsetStart to process them in order
  const sortedHighlights = [...filteredHighlightedTexts].sort(
    (a, b) => a.offsetStart - b.offsetStart
  );

  // Create an array to track which characters are highlighted and by which highlight
  const textLength = text.length;
  const highlightMap: Array<{ numbers: number[]; starts: Set<number> } | null> = new Array(
    textLength
  ).fill(null);

  // Mark all highlighted ranges
  sortedHighlights.forEach((highlight) => {
    for (let i = highlight.offsetStart; i < Math.min(highlight.offsetEnd, textLength); i++) {
      if (highlightMap[i] === null) {
        highlightMap[i] = { numbers: [], starts: new Set() };
      }
      // Track all active highlights at this position
      highlightMap[i]!.numbers.push(highlight.number);
      // Track which highlights start at this position
      if (i === highlight.offsetStart) {
        highlightMap[i]!.starts.add(highlight.number);
      }
    }
  });

  // Build the parts array by grouping consecutive characters with the same highlight state
  const parts: Array<{
    text: string;
    isHighlighted: boolean;
    numbers?: number[];
    starts?: number[];
  }> = [];
  let currentPart: {
    text: string;
    isHighlighted: boolean;
    numbers?: number[];
    starts?: number[];
  } | null = null;

  for (let i = 0; i < textLength; i++) {
    const highlightInfo = highlightMap[i];
    const isHighlighted = highlightInfo !== null;
    const numbers = highlightInfo?.numbers || [];
    const starts = highlightInfo?.starts || new Set();

    // Create a sorted, unique array of numbers for comparison
    const sortedNumbers = [...new Set(numbers)].sort((a, b) => a - b);
    const sortedStarts = [...starts].sort((a, b) => a - b);

    if (currentPart === null) {
      // Start a new part
      currentPart = {
        text: text[i],
        isHighlighted,
        numbers: isHighlighted ? sortedNumbers : undefined,
        starts: isHighlighted ? sortedStarts : undefined,
      };
    } else {
      // Compare current part state with this character's state
      // We compare numbers (active highlights) and highlight state, but not starts
      // because starts only tracks where highlights begin, not where they continue
      const numbersMatch =
        JSON.stringify(currentPart.numbers || []) === JSON.stringify(sortedNumbers);

      if (currentPart.isHighlighted === isHighlighted && numbersMatch) {
        // Continue the current part - update starts if this position has new starts
        currentPart.text += text[i];
        // Update starts if there are new highlights starting at this position
        if (isHighlighted && sortedStarts.length > 0) {
          const currentStarts = currentPart.starts || [];
          const mergedStarts = [...new Set([...currentStarts, ...sortedStarts])].sort(
            (a, b) => a - b
          );
          currentPart.starts = mergedStarts;
        }
      } else {
        // End current part and start a new one
        parts.push(currentPart);
        currentPart = {
          text: text[i],
          isHighlighted,
          numbers: isHighlighted ? sortedNumbers : undefined,
          starts: isHighlighted ? sortedStarts : undefined,
        };
      }
    }
  }

  // Add the last part
  if (currentPart !== null) {
    parts.push(currentPart);
  }

  const firstHighlightIndex = parts.findIndex(
    (p) => p.isHighlighted && p.starts && p.starts.length > 0
  );

  return (
    <>
      {parts.map((part, index) => {
        if (part.isHighlighted && part.numbers && part.numbers.length > 0) {
          // Only show numbers for highlights that start at this position
          const numbersToShow = part.starts ? part.starts : [];
          return (
            <Box
              key={`highlight-${index}`}
              component="span"
              data-highlight-start={index === firstHighlightIndex ? 'true' : undefined}
              sx={{
                backgroundColor:
                  part.numbers && part.numbers.length > 1
                    ? theme.palette.secondary.main // overlap color
                    : theme.palette.lightblue[200], // normal highlight

                px: 0.5,
                borderRadius: 0.5,
                fontWeight: 500,
              }}
            >
              {numbersToShow.map((number, numIndex) => (
                <Box
                  key={`number-${number}-${numIndex}`}
                  data-ignore-offset="true"
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    fontSize: '14px',
                    fontWeight: 600,
                    mr: 0.5,
                    lineHeight: 1,
                    verticalAlign: 'middle',
                  }}
                >
                  {number}
                </Box>
              ))}
              {part.text}
            </Box>
          );
        }
        return <span key={`text-${index}`}>{part.text}</span>;
      })}
    </>
  );
};
